import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import deepmerge from 'deepmerge';
import * as echarts from 'echarts';

const colors = ['#007BFF', '#0067D6', '#004DB7', '#003598', '#001F7A', '#01154F'];
export interface ChartEntityLabel {
  label?: string | null;
  bot_name?: string | null;
  bot_visual_name?: string | null;
}

export const combineMerge = (target, source, options) => {
  const destination = target.slice();

  source.forEach((item, index) => {
    if (typeof destination[index] === 'undefined') {
      destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
    } else if (options.isMergeableObject(item)) {
      destination[index] = deepmerge(target[index], item, options);
    } else if (target.indexOf(item) === -1) {
      destination.push(item);
    }
  });
  return destination;
};
@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() loading = true;
  @Input() labels: string[] = [];
  @Input() series: { name: string; data: number[] }[] = [];
  @Input() title = '';
  @Input() chartType: 'bar' | 'line' | 'pie' = 'bar'; // bar, line, pie, doughnut
  @Input() stacked = false;
  @Input() horizontal = false;
  @Input() translateLabels: boolean = false;
  @Input() entityLabels: ChartEntityLabel[] = [];

  @Input() options: echarts.EChartsOption = {};

  chartOption: echarts.EChartsOption;
  chartInstance: echarts.ECharts | null = null;

  isLoading = true;

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  }

  constructor(private translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']) {
      setTimeout(() => {
        this.isLoading = this.loading;
      }, 333);
    }
    if (
      changes['series'] ||
      changes['labels'] ||
      changes['options'] ||
      changes['entityLabels']
    ) {
      this.updateChartOptions();
    }
  }

  ngOnInit(): void {
    this.updateChartOptions();
  }

  updateChartOptions() {
    const categoryAxis: any = {
      type: 'category',
      data: this.labels,
    };

    if (this.entityLabels?.length) {
      categoryAxis.axisLabel = {
        formatter: (value: string, index: number) =>
          this.formatEntityAxisLabel(value, index),
        rich: {
          agentName: {
            color: '#263453',
            fontWeight: 600,
            lineHeight: 18,
          },
          agentTag: {
            color: 'rgba(38, 52, 83, 0.65)',
            backgroundColor: 'rgba(80, 105, 150, 0.1)',
            borderColor: 'rgba(80, 105, 150, 0.2)',
            borderWidth: 1,
            borderRadius: 8,
            padding: [2, 6],
            lineHeight: 16,
          },
        },
      };
    }

    const options = {
      title: {
        text: this.title,
        show: false,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: this.entityLabels?.length
          ? (params: any) => this.formatEntityTooltip(params)
          : undefined,
      },

      legend: {
        data: this.series?.map((d) => {
          return {
            name: this.translateLabels
              ? this.translateService.instant(`charts.labels.${d.name}`)
              : d?.name || (d as any)?.label,
          };
        }),
      },
      [this.horizontal ? 'yAxis' : 'xAxis']: categoryAxis,
      [this.horizontal ? 'xAxis' : 'yAxis']: {
        type: 'value',

        // name: 'Value',
      },
      series: this.series?.map((ds: any, i) => ({
        ...ds,
        name: this.translateLabels
          ? this.translateService.instant(`charts.labels.${ds.name}`)
          : ds.name,
        type: this.chartType,
        smooth: 0.5, // Makes the line curve smoothly
        lineStyle: {
          width: 3, // Thicker line
        },
        itemStyle:
          this.chartType === 'line'
            ? {
                borderColor: '#007BFF',
                borderWidth: 4,
              }
            : {},
      })),
    };

    this.chartOption = deepmerge(options, this.options, {
      arrayMerge: combineMerge,
    });
  }

  ngOnDestroy(): void {}

  onChartInit(ec: any) {
    this.chartInstance = ec;
  }

  private formatEntityAxisLabel(value: string, index: number): string {
    const meta = this.entityLabels?.[index];
    if (!meta?.bot_name && !meta?.bot_visual_name) {
      return value;
    }
    const displayName = (meta.bot_visual_name || meta.label || value || '').trim();
    const internalName = (meta.bot_name || '').trim();
    const safeDisplayName = this.escapeEchartsRichText(displayName || value);
    if (!internalName || internalName === displayName) {
      return `{agentName|${safeDisplayName}}`;
    }
    return `{agentName|${safeDisplayName}}\n{agentTag|${this.escapeEchartsRichText(internalName)}}`;
  }

  private formatEntityTooltip(params: any): string {
    const rows = Array.isArray(params) ? params : [params];
    const first = rows[0] || {};
    const meta = this.entityLabels?.[first.dataIndex] || {};
    const fallbackLabel = first.axisValueLabel || first.name || '';
    const displayName = meta.bot_visual_name || meta.label || fallbackLabel;
    const internalName = meta.bot_name || '';
    const title = internalName && internalName !== displayName
      ? `${this.escapeHtml(displayName)} <span style="display:inline-block;margin-left:6px;padding:2px 7px;border-radius:999px;border:1px solid rgba(80,105,150,.2);background:rgba(80,105,150,.1);color:rgba(38,52,83,.72);font-size:10px;font-weight:700;">${this.escapeHtml(internalName)}</span>`
      : this.escapeHtml(displayName);
    const body = rows
      .map((row) => {
        const marker = row.marker || '';
        return `<div>${marker}${this.escapeHtml(row.seriesName || '')}: ${this.escapeHtml(String(row.value ?? ''))}</div>`;
      })
      .join('');
    return `<div><div style="margin-bottom:6px;font-weight:700;">${title}</div>${body}</div>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeEchartsRichText(value: string): string {
    return value.replace(/[{}]/g, '');
  }
}
